'use client';

import { Stack, Theme } from '@mui/material';
import { formatToDDMMMYYYYHHMMAAA } from '@verifime/utils';
import { ReactElement } from 'react';
import NameValueDisplay from '../NameValueDisplay';

export type TEvidenceProvenanceProps = {
  /** ISO timestamp of when the customer finished the capture. Omit if they have not. */
  completedOn?: string;
  /** ISO timestamp of the latest request. Omit for a capture nobody requested. */
  requestedOn?: string;
  /**
   * Who raised the latest request, as their email address. Omit for the initial system-generated
   * flow, which nobody raised: it renders a dash rather than the word "System", because naming a
   * person who does not exist is worse than saying nothing.
   */
  requestedBy?: string;
};

/** Agreed in review over "N/A" and over leaving the value blank. */
const MISSING_VALUE = '-';

function formatTimestamp(value?: string): string {
  return value ? formatToDDMMMYYYYHHMMAAA(value) : MISSING_VALUE;
}

/**
 * The evidence dialog's provenance row: **Completed on**, **Requested on** and **Requested by**,
 * always all three.
 *
 * Every field is permanent, and a value this component does not hold renders as a dash rather than
 * the field disappearing. A field that appears and disappears with the data makes the panel a
 * different shape every time it is opened, and a client cannot tell an absent field from one they
 * have not scrolled to. So a capture taken during onboarding, with no request behind it, still shows
 * all three rows, two of them dashes.
 *
 * The values are always those of the **latest** request. Earlier ones are retained upstream but not
 * shown here, and no history list has been asked for.
 *
 * **Takes no `data-testid`.** It is a row inside the evidence dialog, which carries the page-unique
 * id, so per ADR 72 it is reached from there. Its three fields are permanent and each is labelled,
 * so a test asserts on the label it cares about rather than on the row as an opaque box.
 */
export default function EvidenceProvenance({
  completedOn,
  requestedOn,
  requestedBy,
}: Readonly<TEvidenceProvenanceProps>): ReactElement {
  return (
    <Stack direction="row" flexWrap="wrap" sx={(t: Theme) => ({ gap: t.semantic.spacing.base })}>
      <Stack sx={{ flex: '1 1 40%' }}>
        <NameValueDisplay name="Completed on" value={formatTimestamp(completedOn)} />
      </Stack>
      <Stack sx={{ flex: '1 1 40%' }}>
        <NameValueDisplay name="Requested on" value={formatTimestamp(requestedOn)} />
      </Stack>
      <Stack sx={{ flex: '1 1 40%' }}>
        <NameValueDisplay name="Requested by" value={requestedBy ?? MISSING_VALUE} />
      </Stack>
    </Stack>
  );
}
